import styled, { css, keyframes } from "styled-components";

// The Keepo symbol's two shapes (as in SymbolKeepoIcon): the bar and the "‹"
export const KEEPO_SYMBOL_BAR =
  "M42.008 0H5.5A5.504 5.504 0 0 0 0 5.5v87.81a5.503 5.503 0 0 0 5.5 5.499h36.508c3.034 0 5.5-2.465 5.5-5.5V5.5a5.504 5.504 0 0 0-5.5-5.499";
export const KEEPO_SYMBOL_CHEVRON =
  "M97.481 29.395H80.223c-1.233 0-2.37.474-3.32 1.328L55.19 52.439c-6.354 6.448-6.354 16.784 0 23.232l21.715 21.716c.853.853 2.086 1.327 3.319 1.327H97.48c1.328 0 1.992-1.612 1.043-2.56L66.378 64.103l32.146-32.146c.948-.949.285-2.56-1.043-2.56";

export const breatheChevron = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  /* 14% of the symbol's width (14 of its 100 units) */
  50% {
    transform: translateX(14%);
  }
`;

export const breatheBar = keyframes`
  0%, 100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(0.86);
  }
`;

const Svg = styled.svg<{ $duration: number }>`
  flex-shrink: 0;
  overflow: visible;

  ${({ $duration }) => css`
    .bar {
      transform-origin: 24px 50px;
      animation: ${breatheBar} ${$duration}s ease-in-out infinite;
    }

    .chevron {
      animation: ${breatheChevron} ${$duration}s ease-in-out infinite;
    }
  `}

  @media (prefers-reduced-motion: reduce) {
    .bar,
    .chevron {
      animation: none;
    }
  }
`;

// The arc around the symbol: turning clockwise, and stretching from a dot to 2/3 of the circle and back (the
// circle measures 100, its pathLength, whatever its size)
const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const stretch = keyframes`
  0% {
    stroke-dasharray: 1 100;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 65 100;
    stroke-dashoffset: -15;
  }
  100% {
    stroke-dasharray: 65 100;
    stroke-dashoffset: -99;
  }
`;

const Wrapper = styled.span<{ $size: number }>`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
`;

const Arc = styled.svg`
  position: absolute;
  inset: 0;
  overflow: visible;
  animation: ${spin} 1.6s linear infinite;

  circle {
    fill: none;
    /* Gray: the symbol stays the brightest */
    stroke: var(--color-neutral-150);
    stroke-linecap: round;
    animation: ${stretch} 1.4s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 3s;

    circle {
      stroke-dasharray: 30 100;
      animation: none;
    }
  }
`;

type Props = {
  /** The symbol's width and height, in px (the arc goes around it). */
  size?: number;
  /** Fast while something is happening (loading, publishing); slow as a decoration. */
  speed?: "slow" | "fast";
  color?: string;
  className?: string;
  /** The arc turning around the symbol (on by default). */
  arc?: boolean;
};

/**
 * Keepo's loading indicator: the Keepo symbol "breathing" (its "‹" moves away from the bar and back), with a
 * gray arc turning around it. Takes about twice the symbol's size with the arc.
 */
export const KeepoLoader = ({ size = 16, speed = "fast", color = "currentColor", className, arc = true }: Props) => {
  const symbol = (
    <Svg
      $duration={speed === "fast" ? 1 : 2.4}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill={color}
      className={arc ? undefined : className}
      aria-hidden>
      <path className="bar" d={KEEPO_SYMBOL_BAR} />
      <path className="chevron" d={KEEPO_SYMBOL_CHEVRON} />
    </Svg>
  );
  if (!arc) {
    return symbol;
  }

  // Twice the symbol, never less (a bit more when small, so the arc doesn't touch it)
  const arcSize = Math.max(size * 2, size + 15);
  const strokeWidth = size >= 24 ? 3 : 2;
  return (
    <Wrapper $size={arcSize} className={className} aria-hidden>
      <Arc viewBox={`0 0 ${arcSize} ${arcSize}`}>
        <circle
          cx={arcSize / 2}
          cy={arcSize / 2}
          r={(arcSize - strokeWidth) / 2}
          strokeWidth={strokeWidth}
          pathLength={100}
        />
      </Arc>
      {symbol}
    </Wrapper>
  );
};
